import React, { useState, useCallback, useEffect } from "react";
import { DatePicker, Dropdown, IDropdownOption, Stack } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { SerializedDateFilter } from "../../../store/tableFilters";
import styled from "styled-components";

type DateFilterInputProps = {
  value: SerializedDateFilter;
  onChange: (filter: SerializedDateFilter) => void;
};

export const DateFilterInput = ({ value, onChange }: DateFilterInputProps) => {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState({
    operator: value.operator,
    date1: value.date1 ? new Date(value.date1) : null,
    date2: value.date2 ? new Date(value.date2) : null,
  });

  useEffect(() => {
    setLocalValue({
      operator: value.operator,
      date1: value.date1 ? new Date(value.date1) : null,
      date2: value.date2 ? new Date(value.date2) : null,
    });
  }, [value]);

  const operatorOptions: IDropdownOption[] = [
    { key: "isExact", text: t("ram.table.filter.isExact") },
    { key: "isAfter", text: t("ram.table.filter.isAfter") },
    { key: "isBefore", text: t("ram.table.filter.isBefore") },
    { key: "isBetween", text: t("ram.table.filter.isBetween") },
  ];

  const handleOperatorChange = useCallback(
    (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
      if (option) {
        const newOperator = option.key as SerializedDateFilter["operator"];
        setLocalValue((prev) => ({
          ...prev,
          operator: newOperator,
        }));
        onChange({
          type: "date",
          operator: newOperator,
          date1: localValue.date1?.toISOString() || null,
          date2: localValue.date2?.toISOString() || null,
        });
      }
    },
    [localValue, onChange],
  );

  const handleDate1Select = useCallback(
    (date: Date | null | undefined) => {
      setLocalValue((prev) => ({
        ...prev,
        date1: date || null,
      }));
      onChange({
        type: "date",
        operator: localValue.operator,
        date1: date?.toISOString() || null,
        date2: localValue.date2?.toISOString() || null,
      });
    },
    [localValue, onChange],
  );

  const handleDate2Select = useCallback(
    (date: Date | null | undefined) => {
      setLocalValue((prev) => ({
        ...prev,
        date2: date || null,
      }));
      onChange({
        type: "date",
        operator: localValue.operator,
        date1: localValue.date1?.toISOString() || null,
        date2: date?.toISOString() || null,
      });
    },
    [localValue, onChange],
  );

  const formatDate = (date?: Date): string => {
    if (!date) return "";
    return date.toLocaleDateString();
  };

  return (
    <Container>
      <Stack tokens={{ childrenGap: 8 }}>
        <Dropdown
          selectedKey={localValue.operator}
          options={operatorOptions}
          onChange={handleOperatorChange}
          styles={{
            root: { width: "100%" },
            dropdown: { borderRadius: 4 },
          }}
        />
        <DatePicker
          value={localValue.date1 || undefined}
          onSelectDate={handleDate1Select}
          placeholder={
            localValue.operator === "isBetween"
              ? t("ram.table.filter.startDate")
              : t("ram.table.filter.selectDate")
          }
          formatDate={formatDate}
          styles={{
            root: { width: "100%" },
          }}
        />
        {localValue.operator === "isBetween" && (
          <DatePicker
            value={localValue.date2 || undefined}
            onSelectDate={handleDate2Select}
            placeholder={t("ram.table.filter.endDate")}
            formatDate={formatDate}
            styles={{
              root: { width: "100%" },
            }}
          />
        )}
      </Stack>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

export default DateFilterInput;
